import React from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useMetricsData, useAdminOrders } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/utils';
import { TrendingUp, TrendingDown, DollarSign, Receipt } from 'lucide-react';
export function MetricsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    profitability,
    loading: metricsLoading
  } = useMetricsData(restaurant?.id);
  const {
    orders,
    loading: ordersLoading
  } = useAdminOrders(restaurant?.id);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1020">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-1021" />
      </div>;
  }
  const completedOrders = orders.filter(o => o.status === 'delivered' || o.status === 'ready');
  const totalRevenue = completedOrders.reduce((sum, o) => sum + o.total, 0);
  const averageTicket = completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1022">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-1023">
        <div data-id="element-1024">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1025">
            Métricas y Rentabilidad
          </h1>
          <p className="text-gray-500" data-id="element-1026">Análisis financiero de tu menú</p>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1027">
          <Card data-id="element-1028">
            <CardContent className="p-6" data-id="element-1029">
              <div className="flex items-center justify-between mb-4" data-id="element-1030">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-1031">
                  <DollarSign className="w-5 h-5 text-emerald-600" data-id="element-1032" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1033">
                Ingresos (Recientes)
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1034">
                {formatCurrency(totalRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1035">
            <CardContent className="p-6" data-id="element-1036">
              <div className="flex items-center justify-between mb-4" data-id="element-1037">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1038">
                  <Receipt className="w-5 h-5 text-blue-600" data-id="element-1039" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1040">
                Ticket Promedio
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1041">
                {formatCurrency(averageTicket)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1042">
            <CardContent className="p-6" data-id="element-1043">
              <div className="flex items-center justify-between mb-4" data-id="element-1044">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-1045">
                  <TrendingUp className="w-5 h-5 text-purple-600" data-id="element-1046" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1047">
                Margen Promedio Menú
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1048">
                {profitability.length > 0 ? Math.round(profitability.reduce((sum, p) => sum + p.margin_percent, 0) / profitability.length) : 0}
                %
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Profitability Table */}
        <Card data-id="element-1049">
          <CardHeader data-id="element-1050">
            <CardTitle data-id="element-1051">Rentabilidad por Producto</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1052">
            {metricsLoading ? <div className="space-y-4" data-id="element-1053">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" data-id="element-1054" />)}
              </div> : profitability.length > 0 ? <div className="overflow-x-auto" data-id="element-1055">
                <table className="w-full text-sm text-left" data-id="element-1056">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-1057">
                    <tr data-id="element-1058">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-1059">Producto</th>
                      <th className="px-4 py-3" data-id="element-1060">Categoría</th>
                      <th className="px-4 py-3 text-right" data-id="element-1061">Precio Venta</th>
                      <th className="px-4 py-3 text-right" data-id="element-1062">Costo Estimado</th>
                      <th className="px-4 py-3 text-right" data-id="element-1063">Ganancia Neta</th>
                      <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-1064">
                        Margen
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1065">
                    {profitability.map(item => {
                  const isGoodMargin = item.margin_percent >= 60;
                  const isBadMargin = item.margin_percent < 30;
                  return <tr key={item.menu_item_id} className="border-b border-gray-50 last:border-0" data-id="element-1066">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-1067">
                            {item.item_name}
                          </td>
                          <td className="px-4 py-3 text-gray-500" data-id="element-1068">
                            {item.category || '—'}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-1069">
                            {formatCurrency(item.price)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-1070">
                            {formatCurrency(item.estimated_cost)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-emerald-600" data-id="element-1071">
                            {formatCurrency(item.estimated_margin)}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-1072">
                            <div className="flex items-center justify-end gap-2" data-id="element-1073">
                              <span className={`font-bold ${isGoodMargin ? 'text-emerald-600' : isBadMargin ? 'text-red-600' : 'text-amber-600'}`} data-id="element-1074">
                                {Math.round(item.margin_percent)}%
                              </span>
                              {isGoodMargin ? <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-1075" /> : isBadMargin ? <TrendingDown className="w-4 h-4 text-red-500" data-id="element-1076" /> : null}
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-1077">
                No hay datos de rentabilidad disponibles. Asegúrate de
                configurar los costos en el inventario.
              </div>}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>;
}